// ========================================================
//           CreateEditTradingAccount.js  (FULL)
//           CREATE + EDIT (depending on the route)
// ========================================================
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Switch,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { styled } from "@mui/material/styles";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import { MOCK_TRADING_ACCOUNTS } from "./TradingAccounts"; // adjust the path if needed

const RedConnector = styled(StepConnector)(() => ({
  top: 13,

  "& .MuiStepConnector-line": {
    borderTopWidth: 3,
    borderColor: "#E5E7EB",
    borderRadius: 999,
  },

  "&.Mui-active .MuiStepConnector-line": {
    borderColor: "#DC2626",
  },

  "&.Mui-completed .MuiStepConnector-line": {
    borderColor: "#DC2626",
  },
}));

function RedStepIcon(props) {
  const { active, completed, icon } = props;
  const isOn = active || completed;

  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 12,
        fontWeight: 900,
        color: isOn ? "white" : "#111827",
        background: isOn ? "#DC2626" : "#E5E7EB",
      }}
    >
      {icon}
    </Box>
  );
}

const emptyForm = {
  status: true,

  // STEP 1
  name: "",
  country: "",
  city: "",
  address: "",
  email: "",
  phone: "",
  comment: "",

  // STEP 2
  baseCurrency: "",
  leverage: "",
  password: "",
  investorPassword: "",

  // STEP 3
  linkToTree: false,
  ib: "",
  tree: "",
  server: "",
  group: "",
};

export default function CreateEditTradingAccount() {
  const navigate = useNavigate();
  const location = useLocation();

  // if you enter through /admin/trading-accounts/create -> no accountId
  // if you enter through /admin/trading-accounts/:accountId/edit -> accountId exists
  const { accountId } = useParams();

  const isEdit = Boolean(accountId);

  const steps = useMemo(
    () => ["Personal details", "Account details", "Account allocation"],
    []
  );

  const [activeStep, setActiveStep] = useState(0);

  const [form, setForm] = useState(emptyForm);

  const [showPassword, setShowPassword] = useState(false);
  const [showInvestorPassword, setShowInvestorPassword] = useState(false);

  const maxComment = 255;
  const commentCount = form.comment.length;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Prefill in edit
  useEffect(() => {
    if (!isEdit) return;

    const rowFromState = location?.state?.row;
    const found =
      rowFromState ||
      MOCK_TRADING_ACCOUNTS.find((x) => String(x.id) === String(accountId));

    if (!found) return;

    setForm((prev) => ({
      ...prev,
      status: Boolean(found.status),

      name: found.name || "",
      country: found.country || "",
      city: found.city || "",
      address: found.address || "",
      email: found.email || "",
      phone: found.phone || "",
      comment: found.comment || "",

      baseCurrency: found.baseCurrency || "",
      leverage: found.leverage || "",

      // en edit puedes dejar vacío si no quieres mostrar passwords reales
      password: "",
      investorPassword: "",

      linkToTree: Boolean(found.linkToTree),
      ib: found.ib || "",
      tree: found.tree || "",
      server: found.server || "",
      group: found.group || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, accountId]);

  const stepTitle =
    activeStep === 0
      ? "Personal details"
      : activeStep === 1
      ? "Account details"
      : "Account allocation";

  // Step validations
  const nextDisabled = (() => {
    if (activeStep === 0) return !form.name.trim();

    if (activeStep === 1) {
      // En EDIT usualmente no obligas a reescribir password si no lo cambian
      if (isEdit) return !form.baseCurrency || !form.leverage;
      return (
        !form.baseCurrency ||
        !form.leverage ||
        !form.password.trim() ||
        !form.investorPassword.trim()
      );
    }

    if (activeStep === 2) {
      const base = !form.ib || !form.server;
      const treeReq = form.linkToTree ? !form.tree || !form.group : false;
      return base || treeReq;
    }

    return false;
  })();

  const isLastStep = activeStep === steps.length - 1;

  const handleNext = () => {
    if (!isLastStep) {
      setActiveStep((s) => s + 1);
      return;
    }

    // submit final
    const payload = {
      tradingAccountId: accountId || null,
      ...form,
    };

    if (isEdit) {
      console.log("EDIT SUBMIT", payload);
    } else {
      console.log("CREATE SUBMIT", payload);
    }

    navigate(-1);
  };

  const handleBackStep = () => setActiveStep((s) => Math.max(0, s - 1));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 4,
        pt: 2,
        pb: 4,
        background:
          "radial-gradient(1200px 500px at 50% 0%, #F6EAF7 0%, #F7F9FC 45%, #F7F9FC 100%)",
      }}
    >
      {/* Back */}
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
        onClick={() => navigate(-1)}
        sx={{ textTransform: "none", mb: 1.5, color: "#111827" }}
      >
        Go back
      </Button>

      <Typography
        sx={{
          fontFamily: "Plus Jakarta Sans",
          fontSize: "26px",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "140%",
          color: "#111827",
          mb: 2,
        }}
      >
        {isEdit ? "Edit trading account" : "Create trading account"}
      </Typography>

      {/* Stepper */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box sx={{ width: "75%", minWidth: 520, maxWidth: 900 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<RedConnector />}
            sx={{
              "& .MuiStepLabel-label": { fontSize: 12, color: "#6B7280" },
              "& .MuiStepLabel-label.Mui-active": { color: "#111827", fontWeight: 700 },
              "& .MuiStepLabel-label.Mui-completed": { color: "#111827", fontWeight: 700 },

              ...(activeStep === 0 && {
                "& .MuiStep-root:nth-of-type(2) .MuiStepConnector-line": {
                  position: "relative",
                },
                "& .MuiStep-root:nth-of-type(2) .MuiStepConnector-line::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "50%",
                  height: 3,
                  backgroundColor: "#DC2626",
                  borderRadius: 999,
                  pointerEvents: "none",
                },
              }),
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={RedStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>

      {/* Card */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Paper
          elevation={0}
          sx={{
            width: "75%",
            minWidth: 520,
            maxWidth: 900,
            borderRadius: 3,
            p: 3,
            background: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(229,231,235,0.9)",
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#111827" }}>
              {stepTitle}
            </Typography>

            {/* In your screenshot, the status appears in Step 1 */}
            {activeStep === 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography sx={{ color: "#374151", fontWeight: 700 }}>
                  Account status:
                </Typography>
                <Switch
                  checked={form.status}
                  onChange={(e) => setField("status", e.target.checked)}
                />
              </Box>
            )}
          </Box>

          {/* STEP 1 */}
          {activeStep === 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2.25,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  Name
                </Typography>
                <TextField
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  fullWidth
                  placeholder="Enter a name"
                  size="small"
                  sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  Country
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.country}
                    displayEmpty
                    onChange={(e) => setField("country", e.target.value)}
                    sx={{ borderRadius: 2, background: "white" }}
                  >
                    <MenuItem value="">
                      <span style={{ color: "#9CA3AF" }}>Select an option</span>
                    </MenuItem>
                    <MenuItem value="COL">COL</MenuItem>
                    <MenuItem value="ARG">ARG</MenuItem>
                    <MenuItem value="USA">USA</MenuItem>
                    <MenuItem value="UK">UK</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  City
                </Typography>
                <TextField
                  value={form.city}
                  onChange={(e) => setField("city", e.target.value)}
                  fullWidth
                  placeholder="Enter a city"
                  size="small"
                  sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  Address
                </Typography>
                <TextField
                  value={form.address}
                  onChange={(e) => setField("address", e.target.value)}
                  fullWidth
                  placeholder="Enter an address"
                  size="small"
                  sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  Email
                </Typography>
                <TextField
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  fullWidth
                  placeholder="Enter an email"
                  size="small"
                  sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  Phone
                </Typography>
                <TextField
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  fullWidth
                  placeholder="Enter a phone number"
                  size="small"
                  sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  Comment
                </Typography>

                <TextField
                  value={form.comment}
                  onChange={(e) => setField("comment", e.target.value.slice(0, maxComment))}
                  fullWidth
                  placeholder="Place a consideration"
                  multiline
                  minRows={3}
                  sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 0.75,
                    color: "#6B7280",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {commentCount} de {maxComment}
                </Box>
              </Box>
            </Box>
          )}

          {/* STEP 2 */}
          {activeStep === 1 && (
            <Box sx={{ maxWidth: 720 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2.25,
                }}
              >
                {/* Base currency */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    Base currency
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.baseCurrency}
                      displayEmpty
                      onChange={(e) => setField("baseCurrency", e.target.value)}
                      sx={{ borderRadius: 2, background: "white" }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#9CA3AF" }}>Select an option</span>
                      </MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="GBP">GBP</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Leverage */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    Leverage
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.leverage}
                      displayEmpty
                      onChange={(e) => setField("leverage", e.target.value)}
                      sx={{ borderRadius: 2, background: "white" }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#9CA3AF" }}>Select an option</span>
                      </MenuItem>
                      <MenuItem value="1:10">1:10</MenuItem>
                      <MenuItem value="1:50">1:50</MenuItem>
                      <MenuItem value="1:100">1:100</MenuItem>
                      <MenuItem value="1:200">1:200</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Password */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    Password
                  </Typography>
                  <TextField
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    fullWidth
                    placeholder={isEdit ? "Leave empty to keep current" : "Enter a password"}
                    size="small"
                    type={showPassword ? "text" : "password"}
                    sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowPassword((s) => !s)}
                            size="small"
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Password investor */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    Password investor
                  </Typography>
                  <TextField
                    value={form.investorPassword}
                    onChange={(e) => setField("investorPassword", e.target.value)}
                    fullWidth
                    placeholder={isEdit ? "Leave empty to keep current" : "Enter a password"}
                    size="small"
                    type={showInvestorPassword ? "text" : "password"}
                    sx={{ background: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowInvestorPassword((s) => !s)}
                            size="small"
                          >
                            {showInvestorPassword ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* STEP 3 */}
          {activeStep === 2 && (
            <Box sx={{ maxWidth: 720 }}>
              {/* Title row + switch right */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 800, color: "#111827" }}>
                  Account allocation
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ color: "#6B7280", fontWeight: 700, fontSize: 12 }}>
                    Link the account to a tree:
                  </Typography>
                  <Switch
                    checked={form.linkToTree}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setField("linkToTree", v);
                      if (!v) {
                        setField("tree", "");
                        setField("group", "");
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2.25,
                }}
              >
                {/* IB */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    IB
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.ib}
                      displayEmpty
                      onChange={(e) => setField("ib", e.target.value)}
                      sx={{ borderRadius: 2, background: "white" }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#9CA3AF" }}>Select an option</span>
                      </MenuItem>
                      <MenuItem value="IB-001">IB-001</MenuItem>
                      <MenuItem value="IB-002">IB-002</MenuItem>
                      <MenuItem value="IB-003">IB-003</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Tree */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    Tree
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.tree}
                      displayEmpty
                      disabled={!form.linkToTree}
                      onChange={(e) => setField("tree", e.target.value)}
                      sx={{ borderRadius: 2, background: "white" }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#9CA3AF" }}>Select an option</span>
                      </MenuItem>
                      <MenuItem value="Tree forex">Tree forex</MenuItem>
                      <MenuItem value="Tree indices">Tree indices</MenuItem>
                      <MenuItem value="Tree crypto">Tree crypto</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Server */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    Server
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.server}
                      displayEmpty
                      onChange={(e) => setField("server", e.target.value)}
                      sx={{ borderRadius: 2, background: "white" }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#9CA3AF" }}>Select an option</span>
                      </MenuItem>
                      <MenuItem value="Demo/Agent">Demo/Agent</MenuItem>
                      <MenuItem value="Live/Agent">Live/Agent</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Group */}
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#374151", mb: 0.75 }}>
                    Group
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.group}
                      displayEmpty
                      disabled={!form.linkToTree}
                      onChange={(e) => setField("group", e.target.value)}
                      sx={{ borderRadius: 2, background: "white" }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#9CA3AF" }}>Select an option</span>
                      </MenuItem>
                      <MenuItem value="Demo seas 123">Demo seas 123</MenuItem>
                      <MenuItem value="Demo pro 321">Demo pro 321</MenuItem>
                      <MenuItem value="Live vip 777">Live vip 777</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          )}

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 3,
              gap: 1.25,
            }}
          >
            <Button
              onClick={handleBackStep}
              disabled={activeStep === 0}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.1,
                fontWeight: 800,
                borderColor: "rgba(17,24,39,0.35)",
                color: activeStep === 0 ? "#9CA3AF" : "#111827",
                opacity: activeStep === 0 ? 0.55 : 1,
              }}
            >
              Back
            </Button>

            <Button
              disabled={nextDisabled}
              onClick={handleNext}
              variant="contained"
              color="error"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.1,
                backgroundColor: "#EB0000",
                border: "none",
                fontWeight: 800,
                boxShadow: "none",
                opacity: nextDisabled ? 0.5 : 1,
                "&:hover": { backgroundColor: "#EB0000", border: "none" },
              }}
            >
              {isLastStep ? (isEdit ? "Save changes" : "Create") : "Next step"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
